/* global THREE */
/**
 * Smoke Animation
 * WebGL-based particle system using Three.js
 * Based on: https://codepen.io/shubniggurath/pen/EmNxKJ
 */
export default class SmokeAnimation {
    constructor(canvasElement, panelElement, densityFactor = 1200) {
        // Density factor: container area / desired particle count
        // Higher = fewer particles. 1200 ≈ similar density to stars animation
        this.densityFactor = densityFactor;
        // Accept elements directly (not IDs) for multiple instance support
        this.panel = panelElement;
        this.canvas = canvasElement;

        if (!this.panel || !this.canvas) {
            console.warn('Smoke animation: panel or canvas element not provided');
            return;
        }

        // Get dimensions with fallbacks and validation
        const computedStyle = window.getComputedStyle(this.panel);
        this.width = parseFloat(computedStyle.width) || this.panel.clientWidth || 0;
        this.height = parseFloat(computedStyle.height) || this.panel.clientHeight || 0;

        // Validate dimensions
        if (this.width === 0 || this.height === 0 || Number.isNaN(this.width) || Number.isNaN(this.height)) {
            console.warn('Smoke animation: invalid panel dimensions', this.width, 'x', this.height);
            return;
        }

        // Animation parameters
        this.rotationSpeed = 0.45;
        // Three.js objects (initialized in init())
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.smokeParticles = [];
        this.smokeGeo = null;
        this.smokeMaterials = [];
        this.smokeTexture = null;
        this.clock = null;
        this.light = null;
        // Animation state
        this.animationId = null;
        this.isInitialized = false;
        this.isPaused = false;
        // Resize handler (bound for listener removal)
        this.boundHandleResize = this.handleResize.bind(this);
        // Don't auto-init - controller will call init() when visible
    }

    calculateParticleCount() {
        // Use actual container dimensions (not viewport)
        const containerArea = this.width * this.height;
        const particleCount = Math.floor(containerArea / this.densityFactor);
        // Enforce reasonable bounds: minimum 50, maximum 300
        return Math.max(50, Math.min(300, particleCount));
    }

    /**
     * Update center position based on viewport width
     * Mobile (<768px): Force center to (50, 50) for better visibility
     * Desktop (≥768px): Use data attribute values for custom positioning
     */
    updateCenterPosition() {
        const isMobile = this.width < 768;
        // Mobile: always center (50, 50)
        // Desktop: use data attribute values
        this.centerX = isMobile ? 20 : this.desktopCenterX;
        this.centerY = isMobile ? 80 : this.desktopCenterY;
        // Convert percentage to offset (-500 to 500 range)
        // 0% = -500, 50% = 0, 100% = 500
        this.offsetX = (this.centerX - 50) * 10;
        this.offsetY = (this.centerY - 50) * 10;
    }

    init() {
        // Guard against double-initialization
        if (this.isInitialized) {
            return;
        }
        // Check if Three.js is available
        if (typeof THREE === 'undefined') {
            console.warn('Three.js not loaded, smoke animation disabled');
            return;
        }
        // Check WebGL support
        if (!this.checkWebGLSupport()) {
            console.warn('WebGL not supported, smoke animation disabled');
            return;
        }

        // Get texture URL from data attribute
        const { textureUrl } = this.panel.dataset;
        if (!textureUrl) {
            console.warn('Smoke animation: texture URL not provided in data-texture-url attribute');
            return;
        }

        // Store desktop center values from data attributes (0-100, where 50 is center)
        // These are the "intended" center positions for desktop viewports
        this.desktopCenterX = parseFloat(this.panel.dataset.smokeCenterX) || 50;
        this.desktopCenterY = parseFloat(this.panel.dataset.smokeCenterY) || 50;
        // Calculate responsive center position based on viewport width
        this.updateCenterPosition();
        // Initialize Three.js clock for delta time
        this.clock = new THREE.Clock();
        // Create scene
        this.scene = new THREE.Scene();
        // Create camera
        const aspect = this.width / this.height;
        this.camera = new THREE.PerspectiveCamera(75, aspect, 1, 10000);
        this.camera.position.z = 1000;
        // Create WebGL renderer with alpha transparency
        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: false });
        this.renderer.setSize(this.width, this.height);
        // Load texture using modern TextureLoader API (not ImageUtils from r71)
        const loader = new THREE.TextureLoader();
        this.smokeTexture = loader.load(
            textureUrl,
            // onLoad callback
            () => {
                // Texture loaded successfully, create particles
                this.createSmokeParticles();
                // Add directional light
                this.light = new THREE.DirectionalLight(0xffffff, 1);
                this.light.position.set(-1, 0, 1);
                this.scene.add(this.light);
                // Start animation
                this.isInitialized = true;
                this.animate();
            },
            // onProgress callback
            undefined,
            // onError callback
            (error) => {
                console.error('Smoke animation: failed to load texture', error);
            },
        );

        // Add resize listener
        window.addEventListener('resize', this.boundHandleResize);
    }

    // eslint-disable-next-line class-methods-use-this
    checkWebGLSupport() {
        try {
            const canvas = document.createElement('canvas');
            const hasWebGL = !!(
                window.WebGLRenderingContext
                && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
            );
            return hasWebGL;
        } catch {
            return false;
        }
    }

    createSmokeParticles() {
        const particleCount = this.calculateParticleCount();
        // Create shared geometry (all particles use same geometry)
        this.smokeGeo = new THREE.PlaneGeometry(300, 300);
        // Create two materials with different colors
        // Emerald green (#317500) and turquoise (#005a75)
        const smokeMaterial1 = new THREE.MeshLambertMaterial({
            color: 0x317500, // Emerald green
            map: this.smokeTexture,
            transparent: true,
            opacity: 0.55,
            blending: THREE.AdditiveBlending,
        });
        const smokeMaterial2 = new THREE.MeshLambertMaterial({
            color: 0x005a75, // Turquoise
            map: this.smokeTexture,
            transparent: true,
            opacity: 0.55,
            blending: THREE.AdditiveBlending,
        });
        this.smokeMaterials = [smokeMaterial1, smokeMaterial2];

        // Create particles with alternating materials
        for (let i = 0; i < particleCount; i += 1) {
            // Alternate between two materials
            const material = this.smokeMaterials[i % 2];
            const particle = new THREE.Mesh(this.smokeGeo, material);
            // Random position with center offset applied
            particle.position.set(
                Math.random() * 1000 - 500 + this.offsetX,
                Math.random() * 1000 - 500 + this.offsetY,
                Math.random() * 1000 - 100,
            );

            // Random rotation
            particle.rotation.z = Math.random() * 360;
            this.scene.add(particle);
            this.smokeParticles.push(particle);
        }
    }

    animate() {
        // Check if destroyed or paused
        if (!this.isInitialized || this.isPaused) {
            return;
        }

        // Store animation ID for cleanup
        this.animationId = requestAnimationFrame(() => this.animate());
        // Get delta time
        const delta = this.clock.getDelta();
        // Evolve smoke particles
        this.evolveSmoke(delta);

        // Render scene
        if (this.renderer && this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera);
        }
    }

    evolveSmoke(delta) {
        // Update particle rotations
        for (let i = 0; i < this.smokeParticles.length; i += 1) {
            const particle = this.smokeParticles[i];
            particle.rotation.z += delta * this.rotationSpeed;
        }
    }

    handleResize() {
        if (!this.panel || !this.camera || !this.renderer) return;

        // Store previous offsets to detect breakpoint changes
        const prevOffsetX = this.offsetX;
        const prevOffsetY = this.offsetY;
        // Get new dimensions
        const computedStyle = window.getComputedStyle(this.panel);
        this.width = parseFloat(computedStyle.width) || this.panel.clientWidth || 0;
        this.height = parseFloat(computedStyle.height) || this.panel.clientHeight || 0;

        // Validate
        if (this.width === 0 || this.height === 0 || Number.isNaN(this.width) || Number.isNaN(this.height)) {
            return;
        }

        // Update center position based on new width (mobile vs desktop)
        this.updateCenterPosition();

        // If offsets changed (crossed breakpoint), reposition particles smoothly
        if (
            this.smokeParticles
            && this.smokeParticles.length > 0
            && (prevOffsetX !== this.offsetX || prevOffsetY !== this.offsetY)
        ) {
            const deltaX = this.offsetX - prevOffsetX;
            const deltaY = this.offsetY - prevOffsetY;
            this.smokeParticles.forEach((particle) => {
                particle.position.x += deltaX; // eslint-disable-line no-param-reassign
                particle.position.y += deltaY; // eslint-disable-line no-param-reassign
            });
        }

        // Update camera aspect ratio
        this.camera.aspect = this.width / this.height;
        this.camera.updateProjectionMatrix();
        // Update renderer size
        this.renderer.setSize(this.width, this.height);
    }

    pause() {
        this.isPaused = true;

        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    resume() {
        if (this.isPaused && this.isInitialized) {
            this.isPaused = false;
            this.animate();
        }
    }

    /**
     * Destroy the animation and free resources
     */
    destroy() {
        // Stop animation loop
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        // Remove resize listener
        window.removeEventListener('resize', this.boundHandleResize);

        // Dispose Three.js resources
        if (this.smokeGeo) {
            this.smokeGeo.dispose();
        }
        // Dispose materials
        if (this.smokeMaterials && this.smokeMaterials.length > 0) {
            this.smokeMaterials.forEach((material) => {
                if (material) {
                    material.dispose();
                }
            });
        }
        // Dispose texture
        if (this.smokeTexture) {
            this.smokeTexture.dispose();
        }
        // Remove particles from scene
        if (this.scene && this.smokeParticles && this.smokeParticles.length > 0) {
            this.smokeParticles.forEach((particle) => {
                this.scene.remove(particle);
            });
        }
        // Remove light from scene
        if (this.scene && this.light) {
            this.scene.remove(this.light);
        }
        // Dispose renderer
        if (this.renderer) {
            this.renderer.dispose();
        }

        // Clear arrays and references
        this.smokeParticles = [];
        this.smokeMaterials = [];
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.smokeGeo = null;
        this.smokeTexture = null;
        this.clock = null;
        this.light = null;
        // Reset state
        this.isInitialized = false;
        this.isPaused = false;
    }
}
